/**
 * 진행 중인 시도의 로컬 checkpoint (SPEC 「포함 범위」 3·4).
 *
 * 왜 필요한가. 시도의 끝은 화면이 남긴다 — 다시 시작, 단계 이동, 클리어.
 * 그런데 브라우저를 닫거나 새로고침하면 그 화면이 사라지고, 끝을 남길
 * 기회도 함께 사라진다. 시작만 있고 끝이 없는 시도가 로그에 남으면 "몇 명이
 * 중간에 그만뒀는가"를 셀 수 없다.
 *
 * 그래서 진행 중인 시도를 저장소에 적어 두고, 다음 실행이 그것을 발견하면
 * `interrupted` 종료로 뒤늦게 닫는다. 복구하는 것은 **기록**이지 화면이
 * 아니다 — 판을 이어서 하게 만들지 않는다.
 *
 * 계측 로그와 저장 키를 나눈다. `기록 지우기`가 진행 중인 시도의 근거까지
 * 지우면 안 되고, 반대로 checkpoint가 내보내기 파일에 섞여도 안 된다.
 *
 * 원격 전송이 없다. 이 모듈에도 `fetch`·`sendBeacon`이 없다(AC-25).
 */

import { CHECKPOINT_VERSION, fromCheckpoint, toCheckpoint, type AttemptState } from './attempt';
import { recordingSessionRef, scopedStorage } from './recordingScope';

export const CHECKPOINT_KEY = 'ccatlas.attempt';

export interface CheckpointStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * 브라우저 저장소. 접근 자체가 던질 수 있어(사이트 데이터 차단) 실패하면
 * 아무것도 하지 않는 저장소로 물러난다. checkpoint가 없다고 플레이가 막히지는
 * 않는다 — 복구가 안 될 뿐이다.
 */
const NULL_STORAGE: CheckpointStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

export function resolveCheckpointStorage(): CheckpointStorage {
  try {
    const probe = '__ccatlas_cp_probe__';
    window.localStorage.setItem(probe, '1');
    /*
     * 계측 로그와 같은 이유로 **읽기까지 확인한다**(REV-020). 쓰기만 되고
     * 읽히지 않는 저장소에서는 복구할 checkpoint를 영영 찾지 못하면서도
     * 저장은 성공한 것처럼 보인다.
     *
     * 이 경우의 결과는 "중단 복구가 되지 않는다"이며, 사용자에게는 계측
     * 로그 쪽 안내가 같은 원인을 이미 알린다.
     */
    const echo = window.localStorage.getItem(probe);
    window.localStorage.removeItem(probe);
    return echo === '1' ? window.localStorage : NULL_STORAGE;
  } catch {
    return NULL_STORAGE;
  }
}

export interface CheckpointStore {
  /**
   * 시도의 지금 상태를 저장소에 반영한다. **되읽어 확인했으면** `true`.
   *
   * - 진행 중인 시도: 적고, 적힌 값이 그대로 읽히는지 본다.
   * - 끝난 시도: 다음 실행의 복구 대상에서 뺀다 (REV-030). 지우고 비었는지
   *   되읽는다. 지우기가 던지거나 조용히 무시되면 같은 시도를 **끝난 상태로
   *   덮어 적고** 그것을 되읽는다. 다른 시도의 진행 중 checkpoint가 적혀 있으면
   *   건드리지 않는다 —— 살아 있는 시도의 복구 능력을 버리지 않기 위해서다.
   *
   * 실패해도 던지지 않는다. 저장 실패가 플레이를 막지는 않는다.
   */
  save(state: AttemptState): boolean;
  /**
   * 복구할 진행 중 시도. 없거나, 모양이 맞지 않거나, **끝난 시도로 적혀 있으면**
   * `undefined`.
   */
  read(): AttemptState | undefined;
  /**
   * checkpoint를 지운다. 지운 뒤 **비었음을 되읽어 확인했으면** `true`.
   *
   * 무엇이 적혀 있는지 모르는 채로 끝난 상태로 덮지는 않는다. 그러면 살아 있는
   * 시도의 복구 능력을 조용히 버리게 된다. 특정 시도를 정리하려면 그 시도의
   * 끝난 상태로 `save()`한다.
   */
  clear(): boolean;
}

/** 읽기 자체가 막혔다는 표식. `null`(비어 있다)과 구별한다. */
const UNREADABLE = Symbol('unreadable');

export function createCheckpointStore(
  storage: CheckpointStorage = resolveCheckpointStorage(),
): CheckpointStore {
  function rawItem(): string | null | typeof UNREADABLE {
    try {
      return storage.getItem(CHECKPOINT_KEY);
    } catch {
      return UNREADABLE;
    }
  }

  /** 적고, 적힌 값이 그대로 읽히는지 본다. 쓰기도 조용히 무시될 수 있다. */
  function writeVerified(value: string): boolean {
    try {
      storage.setItem(CHECKPOINT_KEY, value);
    } catch {
      return false;
    }
    return rawItem() === value;
  }

  /**
   * 지우고, 비었는지 되읽는다 (REV-030).
   *
   * **던지지 않았다고 지워진 것은 아니다.** 예전에는 호출이 성공하면 지웠다고
   * 믿었고, 삭제를 조용히 무시하는 저장소에서 클리어한 시도의 checkpoint가
   * 남아 다음 실행이 그 시도를 `interrupted`로 한 번 더 닫았다.
   */
  function removeVerified(): boolean {
    try {
      storage.removeItem(CHECKPOINT_KEY);
    } catch {
      return false;
    }
    return rawItem() === null;
  }

  function parse(raw: string): AttemptState | undefined {
    try {
      return fromCheckpoint(JSON.parse(raw));
    } catch {
      return undefined;
    }
  }

  return {
    save(state) {
      const checkpoint = toCheckpoint(state);
      if (checkpoint !== undefined) {
        return writeVerified(JSON.stringify(checkpoint));
      }

      const raw = rawItem();
      if (raw === null) {
        // 적힌 것이 없다. 이 시도를 복구할 실행은 없다.
        return true;
      }
      if (typeof raw === 'string') {
        const stored = parse(raw);
        if (stored !== undefined && !stored.finished && stored.attemptId !== state.attemptId) {
          /*
           * 이미 다른 시도가 진행 중으로 적혀 있다. 그것을 지우거나 덮으면 살아
           * 있는 시도가 복구 대상에서 빠진다. 이 시도는 적혀 있지 않으므로
           * 정리할 것도 없다.
           */
          return true;
        }
      }

      if (removeVerified()) {
        return true;
      }
      /*
       * 지우지 못했거나 지워졌는지 확인하지 못했다. **끝난 시도로 덮어 적는다.**
       * `read()`는 끝난 시도를 복구 대상으로 내놓지 않으므로, 삭제만 막힌
       * 저장소에서도 다음 실행이 이 시도를 다시 닫지 않는다. 형식은 기존
       * checkpoint 그대로이며 `finished`만 `true`다.
       */
      return writeVerified(JSON.stringify({ version: CHECKPOINT_VERSION, state }));
    },

    read() {
      const raw = rawItem();
      if (typeof raw !== 'string') {
        return undefined;
      }
      const state = parse(raw);
      // 끝난 시도는 복구할 것이 없다. 다시 닫으면 종료가 두 번 붙는다(REV-030).
      return state !== undefined && !state.finished ? state : undefined;
    },

    clear() {
      return removeVerified();
    },
  };
}

/**
 * 기록 출처마다 하나인 checkpoint 저장소 (R2-REV-001).
 *
 * 연습 탭의 열린 시도를 참가 세션 화면이 자기 시도로 보고 닫거나 경로 조각을 만들지
 * 않게, 세션의 checkpoint는 세션 키 공간에만 둔다.
 */
const stores = new Map<string, CheckpointStore>();

export function checkpointsFor(sessionRef: string | undefined): CheckpointStore {
  const id = sessionRef ?? '';
  let store = stores.get(id);
  if (store === undefined) {
    const storage = resolveCheckpointStorage();
    store = createCheckpointStore(sessionRef === undefined ? storage : scopedStorage(storage, sessionRef));
    stores.set(id, store);
  }
  return store;
}

/** 지금 기록하는 출처의 checkpoint 저장소. */
export function checkpoints(): CheckpointStore {
  return checkpointsFor(recordingSessionRef());
}
